import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const beaulieuSurMer: ChauffeurPage = {
  slug: "beaulieu-sur-mer",
  title: "Chauffeur privé à Beaulieu-sur-Mer – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Beaulieu-sur-Mer : Villa Kérylos, Baie des Fourmis, port de plaisance, La Réserve et transfert aéroport de Nice en ~25 min.",
  keywords: [
    "chauffeur privé beaulieu-sur-mer",
    "vtc beaulieu-sur-mer",
    "chauffeur villa kérylos",
    "transfert beaulieu aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Beaulieu-sur-Mer",
    subtitle:
      "La station au luxe discret, entre Villa Kérylos et Baie des Fourmis : un chauffeur pour la villégiature du bord de mer et les corniches.",
    keywords: [
      "VTC Beaulieu-sur-Mer",
      "chauffeur Villa Kérylos",
      "transfert aéroport Nice",
      "chauffeur Cap-Ferrat",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Beaulieu-sur-Mer",
    badges: ["Villa Kérylos", "Port de plaisance", "~25 min de l’aéroport"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Beaulieu-sur-Mer",
    items: [
      {
        title: "Villa Kérylos & bord de mer",
        description:
          "Dépose au pied de la villa grecque et de la Baie des Fourmis, promenade du littoral à votre rythme, reprise sans contrainte de stationnement en front de mer.",
      },
      {
        title: "Luxe discret & palaces",
        description:
          "La Réserve de Beaulieu et les villas Belle Époque : un accueil feutré et un chauffeur à la hauteur d’une clientèle exigeante.",
      },
      {
        title: "Port & Cap-Ferrat",
        description:
          "Du port de plaisance à la promenade Maurice Rouvier qui rejoint Saint-Jean-Cap-Ferrat : liaisons courtes et confortables au fil de la côte.",
      },
      {
        title: "À ~25 minutes de l’aéroport",
        description:
          "La plus courte des liaisons de nos villes vers l’aéroport de Nice, par la basse corniche ou l’A8, avec suivi de vol.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Beaulieu-sur-Mer",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Beaulieu-sur-Mer ⇄ aéroport de Nice en ~25 min, avec suivi de vol et accueil personnalisé à l’arrivée.",
      },
      {
        title: "Villa Kérylos & Baie des Fourmis",
        text: "Dépose devant la villa grecque et le long de la baie, avec temps d’attente pour la visite et la promenade.",
      },
      {
        title: "Palaces & dîners",
        text: "La Réserve de Beaulieu et les tables du front de mer : aller-retour de soirée sans reprendre le volant.",
      },
      {
        title: "Port & yachting",
        text: "Liaison avec le port de plaisance pour équipages et invités, transferts vers Monaco et les autres ports de la côte.",
      },
      {
        title: "Mise à disposition & corniches",
        text: "À l’heure ou à la journée pour relier Cap-Ferrat, Villefranche, Èze et Monaco par les trois corniches.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Villa Kérylos",
        description: "Villa grecque reconstituée, en bord de Baie des Fourmis.",
      },
      {
        name: "Baie des Fourmis",
        description:
          "Anse abritée et sa promenade, cœur balnéaire de Beaulieu.",
      },
      {
        name: "Port de plaisance",
        description: "Marina de Beaulieu, quais et embarquements.",
      },
      {
        name: "La Réserve de Beaulieu",
        description: "Palace historique en front de mer.",
      },
      {
        name: "Gare de Beaulieu-sur-Mer",
        description: "Halte ferroviaire sur la ligne Nice ⇄ Menton et Monaco.",
      },
      {
        name: "Promenade Maurice Rouvier",
        description:
          "Sentier littoral reliant Beaulieu à Saint-Jean-Cap-Ferrat.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur pour la villégiature du bord de mer",
    subheading:
      "Beaulieu-sur-Mer cultive un luxe feutré, à l’abri de la Baie des Fourmis et de son microclimat.",
    paragraphs: [
      "Nichée entre Villefranche et Cap-d’Ail, Beaulieu-sur-Mer aligne villas Belle Époque, marina et palaces sous un microclimat réputé. Le long du front de mer et près de la Villa Kérylos, un chauffeur vous dépose au plus près et reste joignable, ce qui évite la recherche d’une place en saison.",
      "KS Rent Services applique des forfaits fixes annoncés à l’avance — dont Beaulieu-sur-Mer ⇄ aéroport de Nice — et la mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, sont notés 5,0/5 sur 38 avis Google.",
      "La position de Beaulieu, à égale distance de Nice et de Monaco, en fait une base idéale pour rayonner par les corniches : Cap-Ferrat tout proche, Èze en surplomb, Monaco à quelques minutes. Le chauffeur choisit l’itinéraire selon l’heure et le trafic.",
    ],
    bullets: [
      "Dépose au bord de mer, chauffeur joignable en continu",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Beaulieu-sur-Mer",
    steps: [
      "Indiquez votre programme : transfert aéroport, Villa Kérylos, port ou corniches.",
      "Nous confirmons le véhicule et le forfait ou la mise à disposition.",
      "Le chauffeur vous prend en charge à votre villa, hôtel ou sur le port.",
      "Ajustements en direct selon vos horaires et le trafic sur la côte.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Beaulieu-sur-Mer"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Environ 25 minutes, la plus courte de nos liaisons, par la basse corniche ou l’A8. Le chauffeur suit votre vol et cale l’heure de départ en conséquence.",
    },
    {
      q: "Pouvez-vous nous conduire à la Villa Kérylos et attendre ?",
      a: "Oui. Nous vous déposons devant la villa grecque, au bord de la Baie des Fourmis, avec temps d’attente inclus pour la visite et la promenade du littoral.",
    },
    {
      q: "Assurez-vous les liaisons avec le port et Cap-Ferrat ?",
      a: "Oui, transferts depuis le port de plaisance pour équipages et invités, et liaisons courtes vers Saint-Jean-Cap-Ferrat, Villefranche et Monaco.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez tôt en haute saison et lors des escales de yachts ; les demandes de dernière minute sont honorées selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle des palaces et des villas de Beaulieu.",
    },
    {
      q: "Quels véhicules proposez-vous pour Beaulieu-sur-Mer ?",
      a: "Berline Mercedes Classe E ou S, van 7 places et SUV, selon le nombre de passagers et le niveau de confort souhaité.",
    },
  ],
  relatedLinks: [
    { label: "VTC aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "Chauffeur Monaco, tarifs et trajets", href: "/chauffeur/monaco" },
    { label: "Chauffeur privé Nice", href: "/chauffeur/nice" },
    { label: "Transferts sur la Côte d’Azur", href: "/chauffeur/cote-d-azur" },
  ],
  cta: {
    title: "Votre chauffeur privé à Beaulieu-sur-Mer",
    text: "Transfert aéroport, Villa Kérylos ou corniches : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Beaulieu-sur-Mer — VTC 24h/24",
    description:
      "Chauffeur privé et VTC à Beaulieu-sur-Mer : Villa Kérylos, Baie des Fourmis, port et transfert aéroport de Nice en ~25 min. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/beaulieu-sur-mer",
  },
};

export default beaulieuSurMer;
